/**
 * Care Briefing Panel (DRR-134, DRR-135)
 *
 * "What I need to know before I walk into the room."
 *
 * A Drawer that surfaces the most actionable pre-interaction context for a subject:
 *   - Active tasks (what needs doing now, sorted by priority)
 *   - Recent events (what was done last shift)
 *   - Basic subject info (location, notes from category/description)
 *
 * Designed for frontline staff who need to check context quickly mid-shift without
 * leaving their subject list. For deeper history, "Full Journey" navigates
 * to JourneyPage.
 *
 * Data tier: T1 (attestations) + T2 (operational tasks). No T3/T4.
 * DRR-114 compliant: no duration variance, no staffing ratios.
 */

import {
  Drawer,
  Stack,
  Group,
  Text,
  Badge,
  Card,
  Divider,
  Button,
  ThemeIcon,
  Skeleton,
  ScrollArea,
  Alert,
} from '@mantine/core';
import {
  IconHeartHandshake,
  IconClock,
  IconChecklist,
  IconAlertTriangle,
  IconArrowRight,
  IconCalendarCheck,
} from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';

import {
  useGetTasksBySubjectQuery,
  useSubjectJourneyTimelineQuery,
  GqlTaskStatus,
  GqlTaskPriority,
} from '@/generated/graphql';

// ============================================================================
// CONSTANTS
// ============================================================================

const PRIORITY_COLOR: Record<string, string> = {
  [GqlTaskPriority.CRITICAL]: 'red',
  [GqlTaskPriority.HIGH]: 'orange',
  [GqlTaskPriority.NORMAL]: 'yellow',
  [GqlTaskPriority.LOW]: 'blue',
};

const PRIORITY_ORDER = [
  GqlTaskPriority.CRITICAL,
  GqlTaskPriority.HIGH,
  GqlTaskPriority.NORMAL,
  GqlTaskPriority.LOW,
];

function formatRelativeTime(isoString: string): string {
  const diff = Date.now() - new Date(isoString).getTime();
  const hours = Math.floor(diff / (1000 * 60 * 60));
  if (hours < 1) return 'Less than 1h ago';
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

// ============================================================================
// CARE BRIEFING PANEL
// ============================================================================

interface CareBriefingPanelProps {
  readonly opened: boolean;
  readonly onClose: () => void;
  readonly subjectId: string;
  readonly subjectName: string;
  readonly subjectCategory?: string | null;
  readonly subjectDescription?: string | null;
  readonly subjectLabel: string;
}

export function CareBriefingPanel({
  opened,
  onClose,
  subjectId,
  subjectName,
  subjectCategory,
  subjectDescription,
  subjectLabel,
}: CareBriefingPanelProps) {
  const navigate = useNavigate();

  const { data: taskData, loading: tasksLoading } = useGetTasksBySubjectQuery({
    variables: { subjectActorId: subjectId },
    skip: !opened || !subjectId,
  });

  const { data: timelineData, loading: timelineLoading } = useSubjectJourneyTimelineQuery({
    variables: { subjectActorId: subjectId, limit: 5 },
    skip: !opened || !subjectId,
  });

  const tasks = taskData?.getTasksBySubject ?? [];
  const activeTasks = tasks
    .filter((t) => t.status !== GqlTaskStatus.COMPLETED && t.status !== GqlTaskStatus.CANCELLED)
    .sort((a, b) => PRIORITY_ORDER.indexOf(a.priority) - PRIORITY_ORDER.indexOf(b.priority));

  const criticalCount = activeTasks.filter((t) => t.priority === GqlTaskPriority.CRITICAL).length;
  const recentEvents = timelineData?.subjectJourneyTimeline.events ?? [];
  const lastEvent = recentEvents[0];

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      title={
        <Group gap="sm">
          <ThemeIcon color="teal" variant="light" size={32} radius="xl">
            <IconHeartHandshake size={18} />
          </ThemeIcon>
          <div>
            <Text fw={700} size="sm">{subjectName}</Text>
            {subjectCategory && (
              <Text size="xs" c="dimmed">{subjectCategory}</Text>
            )}
          </div>
        </Group>
      }
      position="right"
      size="md"
      scrollAreaComponent={ScrollArea.Autosize}
    >
      <Stack gap="lg" pb="xl">

        {/* Care note from description */}
        {subjectDescription && (
          <Alert variant="light" color="teal" icon={<IconHeartHandshake size={16} />}>
            {subjectDescription}
          </Alert>
        )}

        {/* Last care event */}
        <div>
          <Group gap="xs" mb="sm">
            <IconClock size={14} />
            <Text size="sm" fw={600}>Last Care Activity</Text>
          </Group>
          {timelineLoading ? (
            <Skeleton height={56} radius="md" />
          ) : lastEvent ? (
            <Card withBorder p="sm">
              <Group justify="space-between" wrap="nowrap">
                <div>
                  <Text size="sm" fw={500} lineClamp={1}>{lastEvent.constellationName}</Text>
                  <Group gap="xs" mt={2}>
                    <Badge
                      size="xs"
                      variant="light"
                      color={lastEvent.status === 'completed' ? 'green' : 'blue'}
                    >
                      {lastEvent.status}
                    </Badge>
                    {lastEvent.taskCount > 0 && (
                      <Text size="xs" c="dimmed">
                        {lastEvent.taskCount} task{lastEvent.taskCount === 1 ? '' : 's'}
                      </Text>
                    )}
                  </Group>
                </div>
                <Text size="xs" c="dimmed" style={{ flexShrink: 0 }}>
                  {formatRelativeTime(lastEvent.lastActivityAt)}
                </Text>
              </Group>
            </Card>
          ) : (
            <Text size="sm" c="dimmed">No recent care activity recorded.</Text>
          )}
        </div>

        <Divider />

        {/* Active tasks */}
        <div>
          <Group justify="space-between" mb="sm">
            <Group gap="xs">
              <IconChecklist size={14} />
              <Text size="sm" fw={600}>Active Tasks</Text>
              {activeTasks.length > 0 && (
                <Badge size="xs" color={criticalCount > 0 ? 'red' : 'blue'} variant="filled">
                  {activeTasks.length}
                </Badge>
              )}
            </Group>
            {criticalCount > 0 && (
              <Group gap={4}>
                <IconAlertTriangle size={14} color="var(--mantine-color-red-6)" />
                <Text size="xs" c="red" fw={600}>{criticalCount} critical</Text>
              </Group>
            )}
          </Group>

          {tasksLoading ? (
            <Stack gap="xs">
              <Skeleton height={48} radius="md" />
              <Skeleton height={48} radius="md" />
              <Skeleton height={48} radius="md" />
            </Stack>
          ) : activeTasks.length === 0 ? (
            <Card withBorder p="sm">
              <Group gap="xs">
                <IconCalendarCheck size={16} color="var(--mantine-color-green-6)" />
                <Text size="sm" c="dimmed">No active tasks — all clear.</Text>
              </Group>
            </Card>
          ) : (
            <Stack gap="xs">
              {activeTasks.map((task) => (
                <Card key={task.id} withBorder p="sm">
                  <Group justify="space-between" wrap="nowrap" align="flex-start">
                    <Stack gap={4} style={{ flex: 1, minWidth: 0 }}>
                      <Text size="sm" fw={500} lineClamp={2}>{task.name}</Text>
                      {task.description && (
                        <Text size="xs" c="dimmed" lineClamp={1}>{task.description}</Text>
                      )}
                      <Group gap="xs">
                        <Badge
                          size="xs"
                          color={PRIORITY_COLOR[task.priority] ?? 'gray'}
                          variant="light"
                        >
                          {task.priority}
                        </Badge>
                        <Badge size="xs" variant="outline" color="gray">
                          {task.status}
                        </Badge>
                        {task.dueAt && (
                          <Text size="xs" c="dimmed">
                            Due {formatRelativeTime(task.dueAt)}
                          </Text>
                        )}
                      </Group>
                    </Stack>
                  </Group>
                </Card>
              ))}
            </Stack>
          )}
        </div>

        <Divider />

        {/* Recent care history */}
        {recentEvents.length > 1 && (
          <div>
            <Group gap="xs" mb="sm">
              <IconClock size={14} />
              <Text size="sm" fw={600}>Recent History</Text>
            </Group>
            <Stack gap="xs">
              {recentEvents.slice(1).map((event) => (
                <Group key={event.eventId} justify="space-between" wrap="nowrap">
                  <Text size="xs" lineClamp={1} style={{ flex: 1 }}>{event.constellationName}</Text>
                  <Group gap="xs" style={{ flexShrink: 0 }}>
                    <Badge size="xs" variant="dot" color={event.status === 'completed' ? 'green' : 'blue'}>
                      {event.taskCount}t
                    </Badge>
                    <Text size="xs" c="dimmed">{formatRelativeTime(event.lastActivityAt)}</Text>
                  </Group>
                </Group>
              ))}
            </Stack>
          </div>
        )}

        {/* Actions */}
        <Group mt="auto" pt="md">
          <Button
            variant="light"
            color="teal"
            leftSection={<IconHeartHandshake size={14} />}
            onClick={() => {
              onClose();
              navigate(`/ferp-core/journey?subject=${subjectId}`);
            }}
            style={{ flex: 1 }}
          >
            Full Journey
          </Button>
          <Button
            variant="subtle"
            rightSection={<IconArrowRight size={14} />}
            onClick={() => {
              onClose();
              navigate(`/ferp-core/tasks?subject=${subjectId}`);
            }}
            style={{ flex: 1 }}
          >
            All Tasks
          </Button>
        </Group>

      </Stack>
    </Drawer>
  );
}

// Re-export subjectLabel prop for downstream consumers
export type { CareBriefingPanelProps };
